/**
 * UUIDv7 Utility Module
 *
 * UUIDv7 is a time-ordered UUID format that provides:
 * - 20-40% faster B-tree inserts at scale (100K+ rows)
 * - Reduced index fragmentation (append-mostly pattern)
 * - Better cache efficiency (hot pages stay in memory)
 * - Time-sortable IDs (first 48 bits = Unix timestamp in ms)
 *
 * Backward compatible with existing UUIDv4:
 * - Same string format (36 chars, hyphens at positions 9,14,19,24)
 * - Zod z.string().uuid() accepts both versions
 * - SQL UUID constraints check only format, not version
 */
import { V7Generator } from 'uuidv7';

export { uuidv7 as generateUUID } from 'uuidv7';

// Single generator instance for timestamp-based UUID generation
// Using a shared generator ensures monotonically increasing UUIDs when
// multiple are generated with the same timestamp
const timestampGenerator = new V7Generator();

/**
 * Generate UUIDv7 with specific timestamp
 *
 * Use this for imports with historical data to preserve time-ordering
 * from the source system. The UUID will sort chronologically based on
 * the provided timestamp.
 *
 * @param timestamp - Date object or Unix timestamp in milliseconds
 * @returns A UUIDv7 string with the specified timestamp embedded
 *
 * @example
 * // For a record created on 2024-01-15
 * const id = generateUUIDFromTimestamp(new Date('2024-01-15'));
 *
 * @example
 * // Using milliseconds
 * const id = generateUUIDFromTimestamp(1705276800000);
 */
export function generateUUIDFromTimestamp(timestamp: Date | number): string {
  const ms = typeof timestamp === 'number' ? timestamp : timestamp.getTime();
  // Use generateOrResetCore with 0 rollback allowance to generate UUID at exact timestamp
  // The generator handles monotonicity for multiple UUIDs at the same timestamp
  return timestampGenerator.generateOrResetCore(ms, 0).toString();
}

/**
 * Extract approximate creation time from a UUIDv7
 *
 * UUIDv7 format: tttttttt-tttt-7xxx-yxxx-xxxxxxxxxxxx
 * First 12 hex characters (48 bits) encode the Unix timestamp in milliseconds.
 *
 * Note: Only works reliably with UUIDv7. Returns null for UUIDv4 or invalid UUIDs.
 *
 * @param uuid - A UUIDv7 string to extract timestamp from
 * @returns Date object representing the embedded timestamp, or null if extraction fails
 *
 * @example
 * const createdAt = extractTimestamp('0190a5e0-8c9a-7000-8000-000000000000');
 * console.log(createdAt); // Date object
 */
export function extractTimestamp(uuid: string): Date | null {
  if (!uuid || typeof uuid !== 'string') return null;

  // Remove hyphens and take first 12 hex chars (48 bits = timestamp)
  const hex = uuid.replace(/-/g, '').substring(0, 12);

  // Validate hex string
  if (hex.length !== 12 || !/^[0-9a-fA-F]+$/.test(hex)) return null;

  const ms = parseInt(hex, 16);
  if (isNaN(ms) || ms < 0) return null;

  // Sanity check: timestamp should be between year 2000 and 2100
  // This helps identify UUIDv4 (random first bits) vs UUIDv7 (timestamp)
  const minMs = new Date('2000-01-01').getTime();
  const maxMs = new Date('2100-01-01').getTime();
  if (ms < minMs || ms > maxMs) return null;

  return new Date(ms);
}
